import { useMemo } from 'react';
import { Box, Link } from '@mui/material';
import { Lock } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { differenceInMinutes } from 'date-fns';
import { SttNoResults } from '../all';
import SttTimetablePiece from './SttTimetablePiece';
import { parseToDate } from '../../lib/utils';

type Facility = {
    id?: number;
    name?: string;
    status?: string;
};

type Piece = {
    ini: string;
    end: string;
    duration: number;
    mark: string;
    title?: string;
    price?: number;
    capacity?: {
        free: number;
        total: number;
    };
    bookings?: { id: number; name?: string }[];
    percentagePaid?: number;
    isGame?: boolean;
    isGamePublic?: boolean;
    fixed?: boolean;
    notes?: string;
    players?: { id: number; name: string; img: { uri: string } }[];
    privilegesData?: unknown;
    timetableRates?: unknown;
    priceShape?: { currency?: string; locale?: string };
};

type Column = {
    facility: Facility;
    pieces: Piece[];
};

type Timetable = {
    count: number;
    columns: Column[];
};

type Props = {
    timetable: Timetable;
    columnWidth?: number;
    onSelectPiece: (column: Column, piece: Piece) => void;
};

/**
 * Extract unique time slots from all columns and sort them
 */
function getTimeSlots(columns: Column[]): string[] {
    const times = new Set<string>();
    columns.forEach((col) => {
        col.pieces.forEach((piece) => {
            times.add(piece.ini);
            times.add(piece.end);
        });
    });
    return Array.from(times).sort((a, b) => {
        const dateA = parseToDate(a);
        const dateB = parseToDate(b);
        if (!dateA || !dateB) return a.localeCompare(b);
        return dateA.getTime() - dateB.getTime();
    });
}

/**
 * Calculate the height in pixels for a time slot based on duration
 * Using 2px per minute as the base, with a minimum of 30px
 */
function calculateSlotHeight(slotStart: string, slotEnd: string): number {
    const start = parseToDate(slotStart);
    const end = parseToDate(slotEnd);
    if (!start || !end) return 30;
    const minutes = differenceInMinutes(end, start);
    return Math.max(minutes * 2, 30);
}

/**
 * Timetable with CSS Grid layout for synchronized rows
 */
export function SttTimetable({
    timetable,
    columnWidth = 100,
    onSelectPiece,
}: Props): React.ReactElement {
    const { t } = useTranslation();

    const timeSlots = useMemo(
        () => getTimeSlots(timetable?.columns ?? []),
        [timetable?.columns],
    );

    const gridRowHeights = useMemo(() => {
        if (timeSlots.length < 2) return '';
        const heights: string[] = [];
        for (let i = 0; i < timeSlots.length - 1; i++) {
            const slotStart = timeSlots[i];
            const slotEnd = timeSlots[i + 1];
            if (slotStart && slotEnd) {
                const height = calculateSlotHeight(slotStart, slotEnd);
                heights.push(`${height}px`);
            }
        }
        return heights.join(' ');
    }, [timeSlots]);

    return (
        <div style={{ height: '57vh', margin: 0, padding: 0 }}>
            <Box
                style={{
                    maxHeight: '100%',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                }}
            >
                {/* Header with facility names */}
                <Box
                    id="timetable-header"
                    className="timetable-grid-header"
                    style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${timetable.count}, ${columnWidth}px)`,
                        width: timetable.count * columnWidth,
                    }}
                >
                    {(timetable?.columns ?? []).map((col) => (
                        <Box
                            key={col.facility.id}
                            className={`column-header ${
                                col.facility?.status === 'ONLY_SC'
                                    ? 'onlySC'
                                    : ''
                            }`}
                        >
                            <Link
                                href={`facilities/${col.facility.id}`}
                                className="hd inline-flex items-center gap-1"
                            >
                                {col.facility?.status === 'ONLY_SC' && (
                                    <Lock
                                        role="img"
                                        aria-label={t(
                                            'const.FS.ACTIVE_ONLY_SC',
                                        )}
                                        fontSize="inherit"
                                        className="shrink-0"
                                    />
                                )}
                                {col.facility?.name}
                            </Link>
                        </Box>
                    ))}
                </Box>

                {/* Grid content with pieces */}
                <Box
                    style={{
                        width: timetable.count
                            ? timetable.count * columnWidth + 18
                            : 200,
                    }}
                >
                    <Box
                        style={{
                            maxHeight: '47vh',
                            overflow: 'auto',
                            overflowX: 'hidden',
                        }}
                    >
                        {timetable && timetable.count > 0 && (
                            <Box
                                className="timetable-grid"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${timetable.count}, ${columnWidth}px)`,
                                    gridTemplateRows: gridRowHeights,
                                }}
                            >
                                {(timetable?.columns ?? []).map(
                                    (col, colIndex) =>
                                        (col.pieces ?? []).map(
                                            (piece, pieceIndex) => (
                                                <SttTimetablePiece
                                                    key={`${col.facility.id}-${pieceIndex}`}
                                                    column={col}
                                                    piece={piece}
                                                    columnIndex={colIndex}
                                                    timeSlots={timeSlots}
                                                    onOpenPiece={onSelectPiece}
                                                />
                                            ),
                                        ),
                                )}
                            </Box>
                        )}

                        {timetable && timetable.count === 0 && (
                            <SttNoResults
                                paper
                                text={t('timetable.noFields')}
                            />
                        )}
                    </Box>
                </Box>
            </Box>
        </div>
    );
}
